import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { QrCodeScanner as QrCodeScannerIcon } from "@mui/icons-material";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import LocalGasStationOutlinedIcon from "@mui/icons-material/LocalGasStationOutlined";
import api from "../../../api/axiosConfig";
import { endpoints } from "../../../api/endpoints";
import { colors } from "../../../theme/colors";

interface StatCardProps {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
}

interface DashboardSummaryResponse {
  total_scans?: number;
  total_test?: number;
  total_active_stations?: number;
}

interface DashboardSummary {
  totalScans: number;
  totalTests: number;
  totalActiveStations: number;
}

const defaultSummary: DashboardSummary = {
  totalScans: 0,
  totalTests: 0,
  totalActiveStations: 0,
};

const numberFormatter = new Intl.NumberFormat("en-IN");

const normalizeDashboardSummary = (payload: unknown): DashboardSummary => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return defaultSummary;
  }

  const response = payload as DashboardSummaryResponse;

  return {
    totalScans:
      typeof response.total_scans === "number" ? response.total_scans : 0,
    totalTests:
      typeof response.total_test === "number" ? response.total_test : 0,
    totalActiveStations:
      typeof response.total_active_stations === "number"
        ? response.total_active_stations
        : 0,
  };
};

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color,
}) => {
  const theme = useTheme();
  const iconBackground = alpha(color, 0.1);
  const iconBorder = alpha(color, 0.16);

  return (
    <Card
      sx={{
        position: "relative",
        overflow: "hidden",
        height: "100%",
        minHeight: 138,
        borderRadius: 3.5,
        border: `1px solid ${alpha(color, 0.08)}`,
        backgroundColor: colors.white,
        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: 5,
          height: "100%",
          backgroundColor: color,
        },
        // '&::after': {
        //   content: '""',
        //   position: 'absolute',
        //   top: -36,
        //   right: -18,
        //   width: 120,
        //   height: 120,
        //   borderRadius: '50%',
        //   background: `radial-gradient(circle, ${alpha(color, 0.12)} 0%, ${alpha(color, 0)} 68%)`,
        //   pointerEvents: 'none',
        // },
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: theme.shadows[6],
        },
      }}
    >
      <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            variant="rounded"
            sx={{
              bgcolor: iconBackground,
              color: color,
              width: 58,
              height: 58,
              borderRadius: 2.25,
              border: `1px solid ${iconBorder}`,
              boxShadow: `inset 0 1px 0 ${alpha(colors.white, 0.7)}`,
              flexShrink: 0,
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "28px",
                lineHeight: 1,
                fontFamily: "'Inter', sans-serif",
                letterSpacing: "-0.04em",
                color: color,
                mb: 0.75,
              }}
            >
              {value}
            </Typography>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "16px",
                fontFamily: "'Inter', sans-serif",
                lineHeight: 1.2,
                color: colors.darkGray,
                mb: 0.35,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.gray,
                fontSize: "11px",
                fontWeight: 400,
                fontFamily: "'Inter', sans-serif",
                lineHeight: 1.25,
              }}
            >
              {subtitle}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const DashboardCards: React.FC = () => {
  const [summary, setSummary] = useState<DashboardSummary>(defaultSummary);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchSummary = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = await api.get(endpoints.dashboard.summary, {
          signal: controller.signal,
        });

        setSummary(normalizeDashboardSummary(response.data));
      } catch (err: any) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return;
        }

        const message =
          err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load dashboard summary.";

        setErrorMessage(message);
        setSummary(defaultSummary);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSummary();

    return () => {
      controller.abort();
    };
  }, []);

  const statCardsData = [
    {
      title: "Total Scans",
      value: numberFormatter.format(summary.totalScans),
      subtitle: "All Stations",
      icon: <QrCodeScannerIcon sx={{ fontSize: 30 }} />,
      color: colors.darkGreen,
    },
    {
      title: "Total Tests",
      value: numberFormatter.format(summary.totalTests),
      subtitle: "All Stations",
      icon: <ScienceOutlinedIcon sx={{ fontSize: 30 }} />,
      color: colors.darkGreen,
    },
    {
      title: "Active Stations",
      value: numberFormatter.format(summary.totalActiveStations),
      subtitle: "Fuel + Testing Centre",
      icon: <LocalGasStationOutlinedIcon sx={{ fontSize: 30 }} />,
      color: colors.orange,
    },
  ];

  return (
    <Box sx={{ px: 2, py: 4, bgcolor: "#F5F5F5" }}>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {statCardsData.map((card, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
            <StatCard
              title={card.title}
              value={isLoading ? "..." : card.value}
              subtitle={card.subtitle}
              icon={card.icon}
              color={card.color}
            />
          </Grid>
        ))}
      </Grid>
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <CircularProgress size={24} sx={{ color: colors.darkGreen }} />
        </Box>
      )}
    </Box>
  );
};

export default DashboardCards;
